import { useEffect, useRef, useState } from 'react'
import { debounce, elInViewPort } from '../../helpers/searchSpringHelpers'
import { SSCarouselResult } from '../../search-spring/types/types'
import { CarouselProduct, fromSearchspring } from '../../search-spring-carousel/carouselProduct'
import { getCarouselProducts, trackProfileOnce } from '../../search-spring-carousel/carouselData'
import ProductCarousel from '../../product-carousel/components/ProductCarousel'

type Props = {
  mount: HTMLElement
}

const SLIDES_PER_VIEW = 3

const parseManualProducts = (raw: string): CarouselProduct[] => {
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    console.error('Compact Shop The Look: could not parse manual products', err)
    return []
  }
}

const CompactShopTheLook = ({ mount }: Props) => {
  const dataset = mount.dataset
  const isSearchspring = dataset.mode === 'searchspring'
  const heading = dataset.heading || ''
  const profile = dataset.profile || ''
  const sku = dataset.productSeed || ''
  const limit = dataset.limit || '10'

  const [products, setProducts] = useState<CarouselProduct[]>(isSearchspring ? [] : parseManualProducts(dataset.products || '[]'))
  const [isReady, setIsReady] = useState(!isSearchspring)
  const elRef = useRef<HTMLDivElement>(null)

  /* Only the column that is actually on screen should report an impression. */
  const trackImpressionWhenVisible = (items: SSCarouselResult[]) => {
    const onScroll = debounce(() => {
      /* A hidden column has a zero-sized rect, which reads as "in viewport". */
      if (!elRef.current || elRef.current.offsetParent === null) return
      if (!elInViewPort(elRef.current, false)) return

      trackProfileOnce('profile.impression', sku, profile, items)
      window.removeEventListener('scroll', onScroll)
    }, 500)

    window.addEventListener('scroll', onScroll)
    onScroll()
  }

  useEffect(() => {
    if (!isSearchspring) return

    let cancelled = false

    getCarouselProducts({ profile, productId: dataset.productId, limit })
      .then((items) => {
        if (cancelled) return

        setProducts(items.map(fromSearchspring))
        setIsReady(true)
        trackProfileOnce('profile.render', sku, profile, items)
        trackImpressionWhenVisible(items)
      })
      .catch(() => setIsReady(true))

    return () => {
      cancelled = true
    }
  }, [])

  if (!isReady || products.length === 0) return null

  const showNavigation = products.length > SLIDES_PER_VIEW

  return (
    <div className="CompactShopTheLook" ref={elRef}>
      {heading && (
        <h2 className="CompactShopTheLook__Heading font-bstSpyre font-light uppercase text-[16px] leading-[120%] tracking-[0.08em] mb-SM md:mb-MD">{heading}</h2>
      )}
      {/* `product-carousel` is what theme.css styles Swiper's own arrows through. */}
      <div className="CompactShopTheLook__Carousel product-carousel relative">
        <ProductCarousel
          products={products}
          source="normalized"
          cardVariant="compact"
          slidesPerView={SLIDES_PER_VIEW}
          spaceBetween={5}
          centerInsufficientSlides={false}
          enabledNavigation={showNavigation}
          observer
          profile={profile}
          sku={sku}
        />
      </div>
    </div>
  )
}

export default CompactShopTheLook
